import { GraduationCap, Quote, Building2, Landmark } from "lucide-react";
import Reveal, { Eyebrow } from "../components/Reveal";
import CtaBand from "../components/CtaBand";
import { LEGAL, FOUNDER_STATIC } from "../data/content";
import { useLang } from "../i18n/LanguageContext";

export default function About() {
  const { t } = useLang();
  const founder = t.founder;

  return (
    <div data-testid="about-page">
      {/* Header */}
      <section className="navy-texture text-white" data-testid="about-hero">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-20 md:py-28">
          <Reveal>
            <Eyebrow dark>{t.company.eyebrow}</Eyebrow>
            <h1 className="mt-6 font-serif-display text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-[1.05] max-w-3xl text-balance">
              {t.about.heroTitle}
            </h1>
            <p className="mt-6 text-lg text-slate-200 max-w-2xl leading-relaxed">
              {t.company.valueProp}
            </p>
          </Reveal>
        </div>
      </section>

      {/* Story */}
      <section className="bg-white" data-testid="about-story">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-20 md:py-28">
          <div className="grid gap-12 lg:grid-cols-12 lg:gap-16 items-start">
            <div className="lg:col-span-5">
              <Reveal>
                <Eyebrow>{t.about.storyEyebrow}</Eyebrow>
                <h2 className="mt-6 font-serif-display text-3xl sm:text-4xl font-bold tracking-tight text-[#1B3A5B] text-balance">
                  {t.about.storyTitle}
                </h2>
              </Reveal>
            </div>
            <div className="lg:col-span-7">
              <Reveal delay={1}>
                <p className="text-slate-600 leading-relaxed text-base md:text-lg">
                  {t.company.intro}
                </p>
                <p className="mt-6 text-slate-600 leading-relaxed">
                  {t.company.approach}
                </p>
              </Reveal>
            </div>
          </div>

          {/* Stats mini band */}
          <div className="mt-16 grid grid-cols-2 lg:grid-cols-4 gap-y-10 gap-x-8 border-t border-slate-200 pt-14">
            {t.stats.map((s, i) => (
              <Reveal key={i} delay={i} className="text-center lg:text-left">
                <div className="font-serif-display text-3xl sm:text-4xl font-bold text-[#C9A15A]">
                  {s.value}
                </div>
                <div className="mt-2 text-xs uppercase tracking-widest text-slate-500 leading-snug">
                  {s.label}
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Founder */}
      <section className="bg-[#F8FAFC] border-y border-slate-200" data-testid="founder-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-20 md:py-28">
          <Reveal>
            <Eyebrow>{t.about.founderEyebrow}</Eyebrow>
            <h2 className="mt-6 font-serif-display text-3xl sm:text-4xl font-bold tracking-tight text-[#1B3A5B]">
              {t.about.founderTitle}
            </h2>
          </Reveal>

          <div className="mt-14 grid gap-10 lg:grid-cols-12 lg:gap-14 items-start">
            <div className="lg:col-span-4">
              <Reveal>
                <div className="relative">
                  <div className="aspect-[4/5] overflow-hidden rounded-sm border border-slate-200">
                    <img
                      src={FOUNDER_STATIC.photo}
                      alt={FOUNDER_STATIC.name}
                      className="h-full w-full object-cover object-[center_15%]"
                    />
                  </div>
                  <div className="mt-5">
                    <h3 className="font-serif-display text-2xl font-bold text-[#1B3A5B]">
                      {FOUNDER_STATIC.name}
                    </h3>
                    <p className="mt-1 text-sm text-[#C9A15A] font-medium">
                      {founder.role}
                    </p>
                  </div>
                </div>
              </Reveal>
            </div>

            <div className="lg:col-span-8">
              <Reveal delay={1}>
                <div className="relative bg-white border border-slate-200 rounded-sm p-8 md:p-10">
                  <Quote className="h-9 w-9 text-[#C9A15A]" />
                  <p className="mt-4 font-serif-display text-xl md:text-2xl italic leading-relaxed text-slate-700">
                    {founder.quote}
                  </p>
                </div>

                <div className="mt-8 grid gap-8 sm:grid-cols-2">
                  <div>
                    <div className="flex items-center gap-2 text-[#1B3A5B]">
                      <GraduationCap className="h-5 w-5 text-[#C9A15A]" />
                      <h4 className="font-semibold uppercase tracking-wide text-sm">
                        {founder.formationLabel}
                      </h4>
                    </div>
                    <ul className="mt-4 space-y-3">
                      {founder.formation.map((f, i) => (
                        <li
                          key={i}
                          className="text-sm text-slate-600 leading-relaxed pl-4 border-l-2 border-[#C9A15A]"
                        >
                          {f}
                        </li>
                      ))}
                    </ul>
                  </div>

                  <div>
                    <div className="flex items-center gap-2 text-[#1B3A5B]">
                      <Building2 className="h-5 w-5 text-[#C9A15A]" />
                      <h4 className="font-semibold uppercase tracking-wide text-sm">
                        {founder.parcoursLabel}
                      </h4>
                    </div>
                    <ul className="mt-4 space-y-5 border-l-2 border-[#C9A15A] pl-5">
                      {founder.parcours.map((p, i) => (
                        <li key={i} className="relative">
                          <span className="absolute -left-[26px] top-1.5 h-3 w-3 rounded-full bg-[#C9A15A] ring-4 ring-[#F8FAFC]" />
                          <div className="text-xs font-semibold uppercase tracking-widest text-[#C9A15A]">
                            {p.period}
                          </div>
                          <div className="mt-1 font-semibold text-[#1B3A5B]">
                            {p.title}
                          </div>
                          <div className="text-sm text-slate-600">{p.detail}</div>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </Reveal>
            </div>
          </div>
        </div>
      </section>

      {/* Legal */}
      <section className="bg-white" data-testid="legal-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-16 md:py-20">
          <Reveal>
            <div className="flex items-center gap-2 text-[#1B3A5B]">
              <Landmark className="h-5 w-5 text-[#C9A15A]" />
              <Eyebrow>{t.about.legalEyebrow}</Eyebrow>
            </div>
            <div className="mt-8 grid gap-6 sm:grid-cols-3">
              {[
                { k: t.about.legalLabels.rccm, v: LEGAL.rccm },
                { k: t.about.legalLabels.ninea, v: LEGAL.ninea },
                { k: t.about.legalLabels.capital, v: LEGAL.capital },
              ].map((item) => (
                <div
                  key={item.k}
                  className="bg-[#F8FAFC] border border-slate-200 rounded-sm p-6"
                >
                  <div className="text-xs uppercase tracking-widest text-slate-500">
                    {item.k}
                  </div>
                  <div className="mt-2 font-serif-display text-xl font-bold text-[#1B3A5B]">
                    {item.v}
                  </div>
                </div>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      <CtaBand />
    </div>
  );
}
